import { readSseStream } from './sse-reader';
import type {
  Attachment,
  ChatStreamEvent,
  HandoffContact,
  PageContext,
  PresignResponse,
  SessionPayload,
} from './types';

export interface ApiClientOptions {
  baseUrl: string;
  jwt: string;
  signal?: AbortSignal;
}

export class ApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

function buildHeaders(jwt: string, extra?: Record<string, string>): Headers {
  const headers = new Headers(extra);
  headers.set('Authorization', `Bearer ${jwt}`);
  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  return headers;
}

async function parseJson<T>(resp: Response): Promise<T> {
  if (!resp.ok) {
    let msg = `HTTP ${resp.status}`;
    try {
      const body = (await resp.json()) as { error?: string };
      if (body?.error) msg = body.error;
    } catch {
      /* ignore */
    }
    throw new ApiError(msg, resp.status);
  }
  return (await resp.json()) as T;
}

export interface SessionRequestExtras {
  sourced?: Record<string, string>;
}

export interface ChatStreamBody {
  conversationId: string | null;
  message: string;
  attachments: Attachment[];
  page?: PageContext;
}

export interface RequestHumanBody {
  reason?: string;
  contact?: HandoffContact;
}

export class WidgetApiClient {
  readonly #baseUrl: string;
  readonly #jwt: string;

  constructor(opts: { baseUrl: string; jwt: string }) {
    this.#baseUrl = opts.baseUrl.replace(/\/+$/, '');
    this.#jwt = opts.jwt;
  }

  async session(
    body: SessionRequestExtras = {},
    signal?: AbortSignal,
  ): Promise<SessionPayload> {
    const payload: Record<string, unknown> = { jwt: this.#jwt };
    if (body.sourced && Object.keys(body.sourced).length > 0) {
      payload.sourced = body.sourced;
    }
    const resp = await fetch(`${this.#baseUrl}/api/widget/session`, {
      method: 'POST',
      headers: buildHeaders(this.#jwt),
      body: JSON.stringify(payload),
      signal,
    });
    return parseJson<SessionPayload>(resp);
  }

  async presignUpload(
    file: { name: string; type: string; size: number },
    signal?: AbortSignal,
  ): Promise<PresignResponse> {
    const resp = await fetch(`${this.#baseUrl}/api/widget/presign-upload`, {
      method: 'POST',
      headers: buildHeaders(this.#jwt),
      body: JSON.stringify({
        filename: file.name,
        content_type: file.type,
        size: file.size,
      }),
      signal,
    });
    return parseJson<PresignResponse>(resp);
  }

  /**
   * Uploads a raw file to the pre-signed URL. Returns when the PUT completes.
   * We intentionally use PUT with the raw body (common S3-compatible contract).
   */
  async uploadToPresignedUrl(
    url: string,
    file: File,
    signal?: AbortSignal,
  ): Promise<void> {
    const resp = await fetch(url, {
      method: 'PUT',
      headers: { 'Content-Type': file.type || 'application/octet-stream' },
      body: file,
      signal,
    });
    if (!resp.ok) {
      throw new ApiError(`Upload failed (HTTP ${resp.status})`, resp.status);
    }
  }

  /**
   * Requests a human agent for an existing conversation. `body.contact` ships
   * the handoff form payload (name / email / business / phone). The endpoint
   * accepts `reason` and `contact` both optional — passing `{}` keeps the
   * old zero-arg behaviour for callers that have nothing to add.
   */
  async requestHuman(
    conversationId: string,
    body: RequestHumanBody = {},
    signal?: AbortSignal,
  ): Promise<void> {
    const payload: Record<string, unknown> = { conversation_id: conversationId };
    if (body.reason !== undefined) payload.reason = body.reason;
    if (body.contact && Object.keys(body.contact).length > 0) {
      payload.contact = body.contact;
    }
    const resp = await fetch(`${this.#baseUrl}/api/widget/request-human`, {
      method: 'POST',
      headers: buildHeaders(this.#jwt),
      body: JSON.stringify(payload),
      signal,
    });
    await parseJson<{ ok: true }>(resp);
  }

  /**
   * Submit captured lead fields for a conversation. Returns the optional
   * close message configured on the service so the widget can echo it back
   * to the user in the chat thread.
   */
  async captureLead(
    conversationId: string,
    fields: Record<string, string>,
    source: 'ai_decision' | 'after_n_turns' | 'on_resolve' = 'ai_decision',
    signal?: AbortSignal,
  ): Promise<{ leadId: string; closeMessage: string | null }> {
    const resp = await fetch(`${this.#baseUrl}/api/widget/capture-lead`, {
      method: 'POST',
      headers: buildHeaders(this.#jwt),
      body: JSON.stringify({ conversation_id: conversationId, fields, source }),
      signal,
    });
    const data = await parseJson<{
      status: 'captured';
      leadId: string;
      closeMessage: string | null;
    }>(resp);
    return { leadId: data.leadId, closeMessage: data.closeMessage };
  }

  /**
   * Submit the answers collected through a scripted flow. The server
   * persists them as a lead (via the existing leads table) and returns the
   * conversation id — creating one on the fly when the script ran before any
   * chat activity.
   */
  async scriptComplete(
    body: {
      conversationId: string | null;
      answers: Record<string, string>;
      trigger: 'on_open' | 'on_handoff';
    },
    signal?: AbortSignal,
  ): Promise<{ conversationId: string; leadId: string | null }> {
    const resp = await fetch(`${this.#baseUrl}/api/widget/script-complete`, {
      method: 'POST',
      headers: buildHeaders(this.#jwt),
      body: JSON.stringify({
        conversation_id: body.conversationId,
        answers: body.answers,
        trigger: body.trigger,
      }),
      signal,
    });
    const data = await parseJson<{
      conversationId: string;
      leadId: string | null;
    }>(resp);
    return data;
  }

  /**
   * Posts a chat message and yields SSE events as they arrive.
   *
   * The caller is responsible for rendering 'delta' events and finalising
   * the message when 'done' is received.
   */
  async *chatStream(
    body: ChatStreamBody,
    signal?: AbortSignal,
  ): AsyncGenerator<ChatStreamEvent, void, unknown> {
    const payload: Record<string, unknown> = {
      conversationId: body.conversationId,
      message: body.message,
      attachments: body.attachments,
    };
    if (body.page) payload.page = body.page;

    const resp = await fetch(`${this.#baseUrl}/api/widget/chat`, {
      method: 'POST',
      headers: buildHeaders(this.#jwt, { Accept: 'text/event-stream' }),
      body: JSON.stringify(payload),
      signal,
    });

    if (!resp.ok) {
      throw new ApiError(`Chat request failed (HTTP ${resp.status})`, resp.status);
    }
    if (!resp.body) {
      throw new ApiError('Empty response body', 500);
    }

    yield* readSseStream<ChatStreamEvent>(resp.body);
  }

  /**
   * Fallback polling endpoint used when Socket.IO is unavailable.
   */
  async pollMessages(
    conversationId: string,
    sinceIso: string,
    signal?: AbortSignal,
  ): Promise<{ messages: { id: string; role: string; text: string; created_at: string }[] }> {
    const url = `${this.#baseUrl}/api/widget/conversations/${encodeURIComponent(
      conversationId,
    )}/messages?since=${encodeURIComponent(sinceIso)}`;
    const resp = await fetch(url, {
      method: 'GET',
      headers: buildHeaders(this.#jwt),
      signal,
    });
    return parseJson(resp);
  }
}
